// MARK:- OS 2.0 drawer/dropdown controller — global event-delegated, no
// wrapping custom element. Replaces the legacy jQuery handleDrawers() in
// src/js/theme.js for OS 2.0 markup paths. Does NOT extend or touch the
// legacy code.
//
// File naming: standalone web modules in this theme use the `component-`
// prefix in the FILE NAME (`assets/component-tabs.js`, `assets/component-drawer.js`,
// `assets/component-banner.js`).
//
// Architecture: hand-edited asset, NOT a webpack/gulp build output. Loaded
// as an ES module from any section that has drawer triggers or drawer
// containers (header, mobile-menu, etc.):
//
//   <script src="{{ 'component-drawer.js' | asset_url }}" type="module"></script>
//
// `type="module"` deduplicates by URL so multiple sections including this
// script only execute it once per page.
//
// Why global event delegation (not a wrapping <as-drawer-controller>):
// triggers in `sections/header.liquid` and drawers in `sections/mobile-menu.liquid`
// are rendered as siblings inside the header section group — there's no shared
// ancestor element a section can own. Document-level delegation lets them find
// each other regardless of section boundaries while keeping the implementation
// simple. Customizer section reloads work for free because delegation hits the
// new DOM nodes naturally.
//
// Why prefixed attributes (data-as-drawer*) instead of unprefixed (data-drawer*):
// master's `templates/index.liquid` (now in /legacy/) uses unprefixed attributes
// driven by the legacy jQuery `handleDrawers()` in src/js/theme.js. With the
// layout cutover landed, master's legacy template no longer renders for the
// storefront, but legacy code in src/js/theme.js still binds on initial page
// load. Prefixed attributes on the new section keep the two systems from
// firing on the same trigger.
//
// Markup contract:
//
//   <button data-as-drawer="cart">Basket</button>
//   <a data-as-drawer="shop-dropdown" data-as-drawer-hover>Shop</a>
//   ...
//   <div id="cart" class="drawer">…</div>
//   <div id="shop-dropdown" class="drawer drawer--dropdown drawer--hover">…</div>
//
// Drawer container markup (.drawer, .drawer--dropdown, .drawer--hover,
// .drawer__close, .drawer__overlay) is unchanged from the legacy snippets.
// Triggers use `data-as-drawer="<id>"` to address a drawer by its element id.
//
// Behavior parity with legacy handleDrawers() (src/js/theme.js:126-181):
// - Click on [data-as-drawer]:not([data-as-drawer-hover]) toggles the drawer.
// - Click on [data-as-drawer="close"] closes any open drawer.
// - Hover on [data-as-drawer-hover] opens the drawer after an 80ms intent delay.
// - Click on .drawer__close, .drawer__overlay, or .header__hover-overlay closes.
// - ESC key closes any open drawer.
// - Click on a .drawer link whose href matches the current URL closes the drawer.

const TRANSITION_MS = 300;
const HOVER_DELAY_MS = 80;

let hoverTimer = null;
let initialized = false;

function getDrawer(type) {
  return document.querySelector(`#${CSS.escape(type)}.drawer, #${CSS.escape(type)}.drawer__sub`);
}

function isSubDrawer(drawer) {
  return !!drawer && drawer.classList.contains('drawer__sub');
}

function closeSubDrawers() {
  document.querySelectorAll('.drawer__sub.is-open').forEach((sub) => {
    sub.classList.remove('is-open');
  });
}

function getHeader() {
  return document.querySelector('.header');
}

// MARK:- Auto-focus on open is limited to typeable text fields. iOS Safari zooms
// the page toward any focused form control whose font-size is under 16px; the
// cart's first control is the 10px purchase-type <select> (or the read-only
// quantity input), so focusing it on open zoomed the viewport. Fields inside a
// closed .drawer__sub are skipped — the sub gets its own focus call when opened.
const FOCUS_FIELD_SELECTOR = 'input[type="search"], input[type="text"], input[type="email"], textarea';

function focusFirstField(container, { excludeSubDrawers = false } = {}) {
  const field = Array.from(container.querySelectorAll(FOCUS_FIELD_SELECTOR)).find((el) => {
    if (el.readOnly || el.disabled) return false;
    if (excludeSubDrawers && el.closest('.drawer__sub')) return false;
    return true;
  });
  if (field) field.focus();
}

function isHeaderHoverOpen() {
  const header = getHeader();
  if (!header) return false;
  return header.classList.contains('is-dropdown-open') || header.classList.contains('is-hover-open');
}

function resetAllDrawers() {
  document.querySelectorAll('.drawer.is-open').forEach((drawer) => {
    drawer.classList.remove('is-open');
    drawer.style.display = 'none';
  });
  document.querySelectorAll('[data-as-drawer]').forEach((trigger) => {
    trigger.classList.remove('is-active');
  });
  const header = getHeader();
  if (header) header.classList.remove('is-dropdown-open', 'is-hover-open');
}

function open(type, { immediate = false } = {}) {
  const drawer = getDrawer(type);
  if (!drawer) return;

  // MARK:- Nested .drawer__sub elements (e.g. mobile-search inside mobile-menu)
  // toggle .is-open only — the legacy theme.css `.drawer__sub` rules own the
  // slide-in transform, display flow, and stacking. The parent drawer stays
  // mounted underneath so closing the sub returns the user to the parent.
  if (isSubDrawer(drawer)) {
    drawer.classList.add('is-open');
    focusFirstField(drawer);
    return;
  }

  resetAllDrawers();

  const header = getHeader();
  if (header && drawer.classList.contains('drawer--dropdown')) {
    header.classList.add('is-dropdown-open');
  }
  if (header && drawer.classList.contains('drawer--hover')) {
    header.classList.add('is-hover-open');
  }

  // MARK:- Slide animation comes from src/scss/components/_drawer.scss.
  // Cart drawer (data-position="right") uses translateX(100%) → 0; mobile-menu
  // (data-position="left") uses translateX(-100%) → 0; dropdowns use
  // translateY(-100%) → 0. Each is gated on the `.drawer.is-open` selector with
  // a `transition: transform var(--transition-duration)` on `.drawer__inner`.
  //
  // For the transition to fire, the browser needs the element painted at its
  // initial state (display: block + transform off-screen) before .is-open
  // applies. Setting display + class in the same frame collapses both states
  // and the slide is skipped. requestAnimationFrame splits them across frames.
  drawer.style.display = 'block';

  if (immediate) {
    drawer.classList.add('is-open');
  } else {
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
    });
  }

  document.querySelectorAll(`[data-as-drawer="${CSS.escape(type)}"]`).forEach((trigger) => {
    trigger.classList.add('is-active');
  });

  focusFirstField(drawer, { excludeSubDrawers: true });
}

function close({ immediate = false } = {}) {
  const drawers = document.querySelectorAll('.drawer.is-open');
  const header = getHeader();

  // Closing the parent also tears down any open sub-drawers it contains.
  closeSubDrawers();

  drawers.forEach((drawer) => {
    drawer.classList.remove('is-open');

    if (immediate) {
      drawer.style.display = 'none';
    } else {
      // Wait for the transform-back animation (defined in CSS) to finish
      // before pulling the drawer out of the layout. If the user re-opens
      // it during the wait, the is-open check skips the display: none.
      setTimeout(() => {
        if (!drawer.classList.contains('is-open')) {
          drawer.style.display = 'none';
        }
      }, TRANSITION_MS);
    }
  });

  setTimeout(() => {
    if (header) header.classList.remove('is-dropdown-open', 'is-hover-open');
  }, immediate ? 0 : TRANSITION_MS - 50);

  document.querySelectorAll('[data-as-drawer]').forEach((trigger) => {
    trigger.classList.remove('is-active');
  });
}

function toggle(type) {
  const drawer = getDrawer(type);
  if (!drawer) return;
  if (isSubDrawer(drawer)) {
    if (drawer.classList.contains('is-open')) {
      drawer.classList.remove('is-open');
    } else {
      open(type);
    }
    return;
  }
  if (drawer.classList.contains('is-open')) {
    close();
  } else {
    open(type);
  }
}

function init() {
  if (initialized) return;
  initialized = true;

  document.addEventListener('click', (event) => {
    // .drawer__sub__close closes only its enclosing sub-drawer, leaving the
    // parent drawer mounted. Match this BEFORE the generic close handler so
    // it doesn't fall through and tear the parent down.
    const subClose = event.target.closest('.drawer__sub__close');
    if (subClose) {
      const sub = subClose.closest('.drawer__sub');
      if (sub) sub.classList.remove('is-open');
      return;
    }

    const trigger = event.target.closest('[data-as-drawer]:not([data-as-drawer-hover])');
    if (trigger) {
      event.preventDefault();
      const type = trigger.dataset.asDrawer;
      if (type === 'close') {
        close();
      } else {
        toggle(type);
      }
      return;
    }

    const closeTarget = event.target.closest('.drawer__close, .drawer__overlay, .header__hover-overlay');
    if (closeTarget) {
      close();
      return;
    }

    const drawerLink = event.target.closest('.drawer a');
    if (drawerLink) {
      const href = drawerLink.getAttribute('href');
      if (href && window.location.href.includes(href)) close();
    }
  });

  document.addEventListener('mouseover', (event) => {
    const trigger = event.target.closest('[data-as-drawer-hover][data-as-drawer]');
    if (!trigger) return;
    if (event.relatedTarget && trigger.contains(event.relatedTarget)) return;

    const type = trigger.dataset.asDrawer;
    clearTimeout(hoverTimer);

    if (isHeaderHoverOpen()) {
      open(type, { immediate: true });
    } else {
      hoverTimer = setTimeout(() => open(type), HOVER_DELAY_MS);
    }
  });

  document.addEventListener('mouseout', (event) => {
    const trigger = event.target.closest('[data-as-drawer-hover]');
    if (!trigger) return;
    if (event.relatedTarget && trigger.contains(event.relatedTarget)) return;
    clearTimeout(hoverTimer);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    // ESC peels off the topmost sub-drawer first; only collapses the parent
    // once no sub is open.
    if (document.querySelector('.drawer__sub.is-open')) {
      closeSubDrawers();
    } else {
      close();
    }
  });
}

init();
